import { useState, useEffect } from "react";
import axios from "axios";
import { Megaphone, Plus, Trash2, Clock, AlertTriangle, Info, AlertCircle, Send } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityConfig = {
  info: { label: "Info", color: "bg-blue-100 text-blue-700", icon: Info, border: "border-blue-200" },
  important: { label: "Important", color: "bg-amber-100 text-amber-700", icon: AlertTriangle, border: "border-amber-200" },
  urgent: { label: "Urgent", color: "bg-red-100 text-red-700", icon: AlertCircle, border: "border-red-200" }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hh}:${mm}`;
};

const AdminAnnouncements = () => {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", priority: "info" });

  useEffect(() => { fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(res.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error("Title and message are required");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/announcements`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement published & emailed to all active agents!");
      setForm({ title: "", message: "", priority: "info" });
      setShowForm(false);
      fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create announcement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="admin-announcements">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-emerald-600" />
              Announcements
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Send announcements to all active agents. Auto-expires after 10 days.
            </p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            data-testid="new-announcement-btn"
          >
            <Plus className="w-4 h-4" /> New Announcement
          </Button>
        </div>

        {/* Create Form */}
        {showForm && (
          <Card className="border-emerald-200 shadow-md" data-testid="announcement-form">
            <CardHeader className="pb-3 border-b border-gray-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="w-5 h-5 text-emerald-600" />
                Create Announcement
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-gray-700">Title</Label>
                  <Input
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="Announcement title..."
                    className="mt-1"
                    data-testid="announcement-title-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Message</Label>
                  <textarea
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder="Write your announcement message..."
                    rows={4}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-y"
                    data-testid="announcement-message-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-2 block">Priority</Label>
                  <div className="flex gap-3" data-testid="priority-selector">
                    {Object.entries(priorityConfig).map(([key, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setForm({ ...form, priority: key })}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                            form.priority === key
                              ? `${cfg.color} ${cfg.border} ring-2 ring-offset-1 ring-current`
                              : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                          }`}
                          data-testid={`priority-${key}-btn`}
                        >
                          <Icon className="w-4 h-4" />
                          {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                    data-testid="publish-announcement-btn"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? "Publishing..." : "Publish & Email Agents"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Announcements List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : announcements.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No announcements yet</p>
              <p className="text-gray-400 text-sm">Create your first announcement to notify all agents</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {announcements.map(a => {
              const cfg = priorityConfig[a.priority] || priorityConfig.info;
              const Icon = cfg.icon;
              return (
                <Card
                  key={a.id}
                  className={`bg-white border-gray-200 shadow-sm ${a.is_expired ? "opacity-60" : ""}`}
                  data-testid={`announcement-${a.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={cfg.color}>
                            <Icon className="w-3 h-3 mr-1" />
                            {cfg.label}
                          </Badge>
                          {a.is_expired && (
                            <Badge className="bg-gray-100 text-gray-500">Expired</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900 text-lg">{a.title}</h3>
                        <p className="text-gray-600 mt-1 whitespace-pre-line text-sm">{a.message}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(a.created_at)}
                          </span>
                          <span>By {a.created_by}</span>
                          <span>Expires: {formatDateTime(a.expires_at)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(a.id)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-announcement-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminAnnouncements;
